import { Player, Position, DepthChartEntry, Sport } from '../types';
import { SPORT_RULESETS } from '../data/sportRulesets';
import { SPORT_POSITIONS } from '../data/sportsPositions';

interface RandomizeOptions {
  activeTab: string;
  sport: Sport;
  teamId: string;
  preserveColors?: boolean;
  ensureFairPlayTime?: boolean;
  absentPlayerIds?: string[]; // Players who are absent and should not be included
  selectedFormation?: string; // Current selected formation context
  standardProtectedPositions?: { [position: string]: string }; // For non-baseball sports
  /** Baseball: fixed DH for all innings when set */
  protectedDhPlayerId?: string;
}

interface RandomizationResult {
  entries: DepthChartEntry[];
  statistics: {
    totalPositionsAssigned: number;
    playersAssigned: number;
    benchedPlayers: number;
    fairPlayTimeScore?: number;
  };
}

/**
 * Service for randomizing depth chart assignments
 * Handles sport-specific rules and ensures fair distribution
 */
export class DepthChartRandomizer {
  /**
   * Main randomization method - routes to appropriate algorithm based on sport and tab
   */
  static randomizeDepthChart(
    players: Player[],
    positions: Position[],
    currentDepthChart: DepthChartEntry[],
    options: RandomizeOptions
  ): RandomizationResult {
    const { sport, activeTab } = options;
    
    console.log('🚨 [SIMPLIFIED] randomizeDepthChart called with:', { sport, activeTab });

    // Route to appropriate randomization algorithm - no more unit mapping complexity!
    if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Fielding') {
      console.log('[randomizeDepthChart] Routing to randomizeBaseballFielding');
      return this.randomizeBaseballFielding(players, positions, currentDepthChart, options);
    } else if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Lineup') {
      console.log('🚨 [SIMPLIFIED] Going to randomizeLineupOrder - no mapping needed');
      return this.randomizeLineupOrder(players, positions, currentDepthChart, options);
    } else {
      console.log('[randomizeDepthChart] Routing to randomizeStandardDepthChart');
      return this.randomizeStandardDepthChart(players, positions, currentDepthChart, options);
    }
  }

  /**
   * Randomize baseball/softball fielding across innings
   */
  private static randomizeBaseballFielding(
    players: Player[],
    positions: Position[],
    currentDepthChart: DepthChartEntry[],
    options: RandomizeOptions & { 
      protectedPositions?: { [inning: number]: { [position: string]: string } };
      teamInnings?: number;
    }
  ): RandomizationResult {
    const { sport, teamId, activeTab, preserveColors = false, protectedPositions = {}, teamInnings, absentPlayerIds = [], selectedFormation, protectedDhPlayerId } = options;
    const ruleset = SPORT_RULESETS[sport];
    const defaultInnings = teamInnings || ruleset.defaultInnings || 7;
    const allDefensePositions = SPORT_POSITIONS[sport]['Defense'] || [];

    console.log('[randomizeBaseballFielding] Using activeTab (database unit):', activeTab);

    if (allDefensePositions.length === 0) {
      throw new Error(`No fielding positions found for ${sport}`);
    }

    const hasDhSlot = allDefensePositions.some(p => p.key === 'DH');
    const dhInUse = Boolean(
      protectedDhPlayerId && !absentPlayerIds.includes(protectedDhPlayerId) && hasDhSlot
    );
    // DH is an optional, off-diamond slot: it only occupies a roster spot when a DH is set.
    // Otherwise bench count follows 9 fielders in excess of available players.
    const fieldingPositions = dhInUse
      ? allDefensePositions
      : allDefensePositions.filter(p => p.key !== 'DH');

    const mergedProtectedPositions: { [inning: number]: { [position: string]: string } } = {};
    for (let inning = 1; inning <= defaultInnings; inning++) {
      const src = protectedPositions[inning] || {};
      const copy: { [pos: string]: string } = {};
      for (const [pos, pid] of Object.entries(src)) {
        if (pos === 'DH' && !dhInUse) continue;
        copy[pos] = pid;
      }
      mergedProtectedPositions[inning] = copy;
    }
    if (dhInUse) {
      for (let inning = 1; inning <= defaultInnings; inning++) {
        mergedProtectedPositions[inning]['DH'] = protectedDhPlayerId!;
      }
    }

    // Filter out absent players
    const availablePlayers = players.filter(player => !absentPlayerIds.includes(player.id));
    const newEntries: DepthChartEntry[] = [];
    const playersById = new Map(availablePlayers.map(p => [p.id, p]));

    const playerInningUsage: Record<string, number[]> = {};
    const playerBenchInnings: Record<string, number[]> = {};

    availablePlayers.forEach(player => {
      playerInningUsage[player.id] = [];
      playerBenchInnings[player.id] = [];
    });

    const protectedAssignments: { playerId: string; position: string; inning: number }[] = [];
    for (let inning = 1; inning <= defaultInnings; inning++) {
      const protectedForInning = mergedProtectedPositions[inning] || {};
      Object.entries(protectedForInning).forEach(([position, playerId]) => {
        if (!absentPlayerIds.includes(playerId)) {
          protectedAssignments.push({ playerId, position, inning });
        }
      });
    }

    const ruleForEntry = SPORT_RULESETS[options.sport];
    const emitEntry = (playerId: string, position: string, inning: number): void => {
      const entry: DepthChartEntry = {
        team_id: options.teamId,
        position,
        depth: inning,
        unit: activeTab,
        player_id: playerId,
        color: preserveColors
          ? (currentDepthChart.find(
              e => e.position === position && e.depth === inning && e.unit === activeTab
            )?.color || '')
          : ''
      };
      if (ruleForEntry.formations.enabled) {
        entry.formation = selectedFormation || '';
      }
      newEntries.push(entry);
    };

    // Priority 1 & 2 & 3: plan who sits each inning (all-slots-filled, one-sit-per-player, battery rest).
    const benchByInning = this.planBenchByInning(
      availablePlayers,
      protectedAssignments,
      defaultInnings,
      fieldingPositions.length
    );

    // Emit protected entries and seed usage/history.
    const playerPositionHistory: Record<string, string[]> = {};
    availablePlayers.forEach(p => { playerPositionHistory[p.id] = []; });
    const playerLastFieldPosition: Record<string, string> = {};

    for (const pa of protectedAssignments) {
      emitEntry(pa.playerId, pa.position, pa.inning);
      playerInningUsage[pa.playerId].push(pa.inning);
    }

    // Per-inning: figure out open positions and field players, then assign with priority 4 & 5.
    for (let inning = 1; inning <= defaultInnings; inning++) {
      const protectedForInning = mergedProtectedPositions[inning] || {};
      const protectedPlayerSet = new Set(Object.values(protectedForInning));
      const bench = benchByInning[inning] || new Set<string>();

      const fieldPlayerIds = availablePlayers
        .map(p => p.id)
        .filter(id => !bench.has(id) && !protectedPlayerSet.has(id));

      const openPositions = fieldingPositions
        .map(p => p.key)
        .filter(key => !protectedForInning[key]);

      const positionAssignments = this.assignOpenPositionsPrioritized(
        fieldPlayerIds,
        openPositions,
        playerPositionHistory,
        playerLastFieldPosition,
        playersById,
        sport
      );

      for (const { playerId, position } of positionAssignments) {
        emitEntry(playerId, position, inning);
        playerInningUsage[playerId].push(inning);
        playerPositionHistory[playerId].push(position);
        playerLastFieldPosition[playerId] = position;
      }

      // Track protected positions as history and last-played for stickiness in later innings.
      for (const [pos, pid] of Object.entries(protectedForInning)) {
        if (absentPlayerIds.includes(pid)) continue;
        playerPositionHistory[pid].push(pos);
        playerLastFieldPosition[pid] = pos;
      }

      // Record bench innings.
      for (const pid of bench) {
        playerBenchInnings[pid].push(inning);
      }
    }

    // Calculate fair play time score
    const fairPlayTimeScore = this.calculateAdvancedFairPlayTimeScore(
      playerInningUsage, 
      playerBenchInnings, 
      defaultInnings
    );
    
    return {
      entries: newEntries,
      statistics: {
        totalPositionsAssigned: newEntries.length,
        playersAssigned: Object.keys(playerInningUsage).filter(playerId => 
          playerInningUsage[playerId].length > 0
        ).length,
        benchedPlayers: Math.max(0, availablePlayers.length - fieldingPositions.length),
        fairPlayTimeScore
      }
    };
  }

  private static isBatteryPosition(position: string): boolean {
    return position === 'P' || position === 'C';
  }

  /**
   * Compute mandatory bench innings for the protected battery.
   *
   * Pitcher rules:
   *   - Starting pitcher (whoever pitches the earliest inning): sits AFTER each consecutive
   *     pitching stretch ends. No "before" rest — they begin the game from inning 1.
   *   - Relief pitchers: sit the inning BEFORE each consecutive pitching stretch begins.
   *     No "after" rest.
   *
   * Catcher rules:
   *   - Every catcher: sits AFTER each consecutive catching stretch ends.
   *     No "before" rest.
   *
   * Any computed bench inning that falls on an inning the player is already playing battery
   * (impossible overlap) is silently removed.
   */
  private static getBatteryRestBenchInnings(
    protectedAssignments: { playerId: string; position: string; inning: number }[],
    totalInnings: number
  ): Record<string, Set<number>> {
    // Separate pitcher vs catcher innings
    const pitcherInnings: Record<string, Set<number>> = {};
    const catcherInnings: Record<string, Set<number>> = {};
    const allBatteryInnings: Record<string, Set<number>> = {};

    for (const a of protectedAssignments) {
      if (!this.isBatteryPosition(a.position)) continue;
      if (!allBatteryInnings[a.playerId]) allBatteryInnings[a.playerId] = new Set();
      allBatteryInnings[a.playerId].add(a.inning);
      if (a.position === 'P') {
        if (!pitcherInnings[a.playerId]) pitcherInnings[a.playerId] = new Set();
        pitcherInnings[a.playerId].add(a.inning);
      } else {
        if (!catcherInnings[a.playerId]) catcherInnings[a.playerId] = new Set();
        catcherInnings[a.playerId].add(a.inning);
      }
    }

    const mustBench: Record<string, Set<number>> = {};
    const addBench = (playerId: string, inn: number) => {
      if (inn < 1 || inn > totalInnings) return;
      if (!mustBench[playerId]) mustBench[playerId] = new Set();
      mustBench[playerId].add(inn);
    };

    // Identify starting pitcher — whoever pitches the lowest inning number.
    let startingPitcherId: string | null = null;
    let earliestInning = Infinity;
    for (const [pid, inns] of Object.entries(pitcherInnings)) {
      const first = Math.min(...inns);
      if (first < earliestInning) { earliestInning = first; startingPitcherId = pid; }
    }

    // Returns [runStart, runEnd] pairs for each consecutive run in the inning set.
    const getRuns = (inns: Set<number>): [number, number][] => {
      const sorted = [...inns].sort((a, b) => a - b);
      const result: [number, number][] = [];
      let i = 0;
      while (i < sorted.length) {
        const start = sorted[i];
        let end = sorted[i];
        while (i + 1 < sorted.length && sorted[i + 1] === end + 1) { i++; end = sorted[i]; }
        result.push([start, end]);
        i++;
      }
      return result;
    };

    // Pitchers
    for (const [playerId, inns] of Object.entries(pitcherInnings)) {
      const isStarter = playerId === startingPitcherId;
      for (const [start, end] of getRuns(inns)) {
        if (isStarter) {
          // Starter: rest AFTER each pitching stretch
          addBench(playerId, end + 1);
        } else {
          // Reliever: rest BEFORE each pitching stretch
          addBench(playerId, start - 1);
        }
      }
    }

    // Catchers: rest AFTER each catching stretch
    for (const [playerId, inns] of Object.entries(catcherInnings)) {
      for (const [, end] of getRuns(inns)) {
        addBench(playerId, end + 1);
      }
    }

    // Remove any bench inning that overlaps with that player's own battery inning.
    for (const playerId of Object.keys(mustBench)) {
      const battery = allBatteryInnings[playerId];
      if (!battery) continue;
      for (const inn of [...mustBench[playerId]]) {
        if (battery.has(inn)) mustBench[playerId].delete(inn);
      }
      if (mustBench[playerId].size === 0) delete mustBench[playerId];
    }

    return mustBench;
  }

  /**
   * Plan which players sit each inning, in strict priority order:
   *   #1 All positions filled (target bench count = available − fielding slots).
   *   #2 Each player sits at most one inning when possible; sort by fewest benches so far.
   *   #3 Pitcher/Catcher rest — sit the inning after a battery stretch or before one.
   *   Additional tiebreakers: avoid back-to-back benches, prefer players with more innings played
   *   so far (they need rest), then stable rotation index.
   */
  private static planBenchByInning(
    availablePlayers: Player[],
    protectedAssignments: { playerId: string; position: string; inning: number }[],
    totalInnings: number,
    fieldingSlotsPerInning: number
  ): Record<number, Set<string>> {
    const benchByInning: Record<number, Set<string>> = {};
    const playerBenchCount: Record<string, number> = {};
    const playerPlayCount: Record<string, number> = {};
    availablePlayers.forEach(p => {
      playerBenchCount[p.id] = 0;
      playerPlayCount[p.id] = 0;
    });

    const protectedByInning: Record<number, Set<string>> = {};
    for (const a of protectedAssignments) {
      if (!protectedByInning[a.inning]) protectedByInning[a.inning] = new Set();
      protectedByInning[a.inning].add(a.playerId);
    }

    const batteryRest = this.getBatteryRestBenchInnings(protectedAssignments, totalInnings);

    const plannedBenchPerInning = Math.max(0, availablePlayers.length - fieldingSlotsPerInning);
    const rotation = this.shuffleArray(availablePlayers.map(p => p.id));
    const rotationIndex = new Map<string, number>(rotation.map((id, i) => [id, i]));

    for (let inn = 1; inn <= totalInnings; inn++) {
      const protectedSet = protectedByInning[inn] || new Set<string>();
      const prevBench = benchByInning[inn - 1] || new Set<string>();

      const candidates = availablePlayers
        .map(p => p.id)
        .filter(id => !protectedSet.has(id));

      // Priority #1: exactly plannedBenchPerInning go to bench (capped by non-protected count).
      const targetBench = Math.min(plannedBenchPerInning, candidates.length);

      if (targetBench <= 0) {
        benchByInning[inn] = new Set();
        candidates.forEach(id => playerPlayCount[id]++);
        for (const id of protectedSet) playerPlayCount[id]++;
        continue;
      }

      const wasBenchedLast = (id: string) => prevBench.has(id);
      const isBatteryRest = (id: string) => batteryRest[id]?.has(inn) ?? false;

      candidates.sort((a, b) => {
        // Priority #1 (hard) — battery rest must always be honored first.
        const ra = isBatteryRest(a) ? 0 : 1;
        const rb = isBatteryRest(b) ? 0 : 1;
        if (ra !== rb) return ra - rb;

        // Priority #2 (hard) — no back-to-back bench innings.
        // A player who was just benched last inning is deprioritized regardless of
        // how many total bench innings others have. This prevents consecutive sits.
        const wa = wasBenchedLast(a) ? 1 : 0;
        const wb = wasBenchedLast(b) ? 1 : 0;
        if (wa !== wb) return wa - wb;

        // Priority #3 (soft) — spread bench time evenly across all players.
        if (playerBenchCount[a] !== playerBenchCount[b]) {
          return playerBenchCount[a] - playerBenchCount[b];
        }

        // Those who've played more consecutive innings need rest sooner.
        if (playerPlayCount[a] !== playerPlayCount[b]) {
          return playerPlayCount[b] - playerPlayCount[a];
        }
        return (rotationIndex.get(a) ?? 0) - (rotationIndex.get(b) ?? 0);
      });

      const bench = new Set<string>(candidates.slice(0, targetBench));
      benchByInning[inn] = bench;

      availablePlayers.forEach(p => {
        if (bench.has(p.id)) playerBenchCount[p.id]++;
        else playerPlayCount[p.id]++;
      });
    }

    return benchByInning;
  }

  /**
   * Player card position groups: empty = can play anywhere; otherwise only listed positions (plus DH when listed).
   */
  private static playerMayPlayFieldPosition(
    player: Player | undefined,
    position: string,
    sport: Sport
  ): boolean {
    if (sport !== 'Baseball' && sport !== 'Softball') return true;
    if (!player) return true;
    const groups = player.eligible_positions;
    if (!groups || groups.length === 0) return true;
    const posNorm = position.toUpperCase();
    return groups.some(g => g.toUpperCase() === posNorm);
  }

  private static readonly BASEBALL_INFIELD_POSITIONS = new Set([
    'P',
    'C',
    '1B',
    '2B',
    '3B',
    'SS'
  ]);
  private static readonly BASEBALL_OUTFIELD_POSITIONS = new Set(['LF', 'CF', 'RF']);

  /** Infield vs outfield vs DH / other for primary-role matching. */
  private static fieldSlotBucket(position: string): 'infield' | 'outfield' | 'neutral' {
    const p = position.toUpperCase();
    if (this.BASEBALL_OUTFIELD_POSITIONS.has(p)) return 'outfield';
    if (this.BASEBALL_INFIELD_POSITIONS.has(p)) return 'infield';
    return 'neutral';
  }

  /**
   * Primary IF/OF focus from player card role_types (exclusive infield vs outfield).
   * Both infield+outfield, neither, or only battery → flexible across IF/OF for randomization.
   */
  private static primaryFieldGroup(
    player: Player | undefined,
    sport: Sport
  ): 'infield' | 'outfield' | 'flexible' {
    if (sport !== 'Baseball' && sport !== 'Softball') return 'flexible';
    if (!player) return 'flexible';
    const roles = (player.role_types || []).map(r => String(r).toLowerCase());
    const hasI = roles.includes('infield');
    const hasO = roles.includes('outfield');
    if (hasI && !hasO) return 'infield';
    if (hasO && !hasI) return 'outfield';
    return 'flexible';
  }

  /** True if player's primary group fits this field slot (neutral slots accept anyone). */
  private static primaryMatchesFieldSlot(
    player: Player | undefined,
    sport: Sport,
    position: string
  ): boolean {
    const primary = this.primaryFieldGroup(player, sport);
    if (primary === 'flexible') return true;
    const bucket = this.fieldSlotBucket(position);
    if (bucket === 'neutral') return true;
    return bucket === primary;
  }

  /**
   * Assign fielding players to open positions using a "most-constrained-first" (MCV) strategy.
   *
   * RULE 1 — position eligibility is a hard constraint:
   *   Players are NEVER placed at positions not in their eligible_positions list.
   *   Flexible players (empty list) can go anywhere.
   *
   * Main pass algorithm (MCV / most-restricted-player-first):
   *   1. Pick the open position with the FEWEST eligible players remaining.
   *      → Fills niche slots before flexible players can accidentally grab them.
   *   2. Among eligible players for that position, pick the one with the FEWEST
   *      eligible positions remaining among all still-open slots.
   *      → Places the most restricted player first so they are never stranded.
   *   3. Tiebreak by score: IF/OF primary match (+100000), position stickiness (+1000), history.
   *
   * Fallback (only for positions where zero eligible players remain):
   *   Apply -500000 penalty for explicit eligible_positions mismatches so that flexible
   *   players (empty array) always fill gaps before restricted players are forced wrong.
   */
  private static assignOpenPositionsPrioritized(
    fieldPlayerIds: string[],
    openPositionKeys: string[],
    playerPositionHistory: Record<string, string[]>,
    playerLastFieldPosition: Record<string, string>,
    playersById: Map<string, Player>,
    sport: Sport
  ): { playerId: string; position: string }[] {
    const assignments: { playerId: string; position: string }[] = [];
    const openPos = new Set<string>(openPositionKeys);
    const availableIds = new Set<string>(fieldPlayerIds);

    const mayPlay = (id: string, pos: string) =>
      this.playerMayPlayFieldPosition(playersById.get(id), pos, sport);
    const primaryOk = (id: string, pos: string) =>
      this.primaryMatchesFieldSlot(playersById.get(id), sport, pos);
    const historyCount = (id: string, pos: string) =>
      (playerPositionHistory[id] || []).filter(p => p === pos).length;
    const uniqCount = (id: string) =>
      new Set(playerPositionHistory[id] || []).size;

    const score = (id: string, pos: string): number => {
      let s = 0;
      if (primaryOk(id, pos)) s += 100000;
      if (playerLastFieldPosition[id] === pos) s += 1000;
      s += historyCount(id, pos) * 10;
      s -= uniqCount(id);
      return s;
    };

    // MCV main pass — strict eligibility.
    while (openPos.size > 0 && availableIds.size > 0) {
      // Step 1: most-constrained position (fewest eligible players).
      let targetPos: string | null = null;
      let fewestEligible = Infinity;
      for (const pos of openPos) {
        let count = 0;
        for (const id of availableIds) {
          if (mayPlay(id, pos)) count++;
        }
        if (count === 0) continue; // no eligible player — defer to fallback
        if (count < fewestEligible) { fewestEligible = count; targetPos = pos; }
      }
      if (targetPos === null) break; // all remaining positions need fallback

      // Step 2: most-restricted eligible player (fewest options left across open positions).
      let bestId: string | null = null;
      let fewestOptions = Infinity;
      let bestScore = -Infinity;
      for (const id of availableIds) {
        if (!mayPlay(id, targetPos)) continue;
        let options = 0;
        for (const p of openPos) { if (mayPlay(id, p)) options++; }
        const s = score(id, targetPos);
        if (options < fewestOptions || (options === fewestOptions && s > bestScore)) {
          fewestOptions = options;
          bestScore = s;
          bestId = id;
        }
      }
      if (!bestId) break;

      assignments.push({ playerId: bestId, position: targetPos });
      openPos.delete(targetPos);
      availableIds.delete(bestId);
    }

    // Fallback: only reached when zero eligible players remain for certain positions.
    // Pass A — flexible players only (empty eligible_positions = can play anywhere).
    // This avoids ever forcing a restricted player into a wrong position while a
    // flexible player is still available.
    let passADone = false;
    while (!passADone && openPos.size > 0 && availableIds.size > 0) {
      let best: { id: string; pos: string; score: number } | null = null;
      for (const pos of openPos) {
        for (const id of availableIds) {
          const eps = playersById.get(id)?.eligible_positions;
          if (eps && eps.length > 0) continue; // skip restricted players in pass A
          const s = score(id, pos);
          if (!best || s > best.score) best = { id, pos, score: s };
        }
      }
      if (!best) { passADone = true; break; }
      assignments.push({ playerId: best.id, position: best.pos });
      openPos.delete(best.pos);
      availableIds.delete(best.id);
    }

    // Pass B — restricted players as absolute last resort. Apply a large penalty
    // for ineligible positions so a player is only placed wrong if there is literally
    // no other way to fill the slot.
    while (openPos.size > 0 && availableIds.size > 0) {
      let best: { id: string; pos: string; score: number } | null = null;
      for (const pos of openPos) {
        for (const id of availableIds) {
          let s = score(id, pos);
          const eps = playersById.get(id)?.eligible_positions;
          if (eps && eps.length > 0 && !eps.some(e => e.toUpperCase() === pos.toUpperCase())) {
            s -= 500000;
          }
          if (!best || s > best.score) best = { id, pos, score: s };
        }
      }
      if (!best) break;
      assignments.push({ playerId: best.id, position: best.pos });
      openPos.delete(best.pos);
      availableIds.delete(best.id);
    }

    return assignments;
  }

  /**
   * Calculate advanced fair play time score considering bench rules
   */
  private static calculateAdvancedFairPlayTimeScore(
    playerInningUsage: Record<string, number[]>,
    playerBenchInnings: Record<string, number[]>,
    totalInnings: number
  ): number {
    const playerIds = Object.keys(playerInningUsage);
    if (playerIds.length === 0) return 0;

    let score = 100;
    
    // Penalize violations of bench rules
    playerIds.forEach(playerId => {
      const benchInnings = playerBenchInnings[playerId] || [];
      const playingInnings = playerInningUsage[playerId] || [];
      
      // Penalty for more than 1 bench inning (target: one bench per player when possible)
      if (benchInnings.length > 1) {
        score -= (benchInnings.length - 1) * 15;
      }
      
      // Penalty for back-to-back bench innings
      for (let i = 0; i < benchInnings.length - 1; i++) {
        if (benchInnings[i + 1] === benchInnings[i] + 1) {
          score -= 20; // Heavy penalty for consecutive bench
        }
      }
      
      // Bonus for even playing time distribution
      const expectedPlayingTime = totalInnings * 0.6; // Roughly 60% playing time
      const actualPlayingTime = playingInnings.length;
      const deviation = Math.abs(actualPlayingTime - expectedPlayingTime);
      score -= deviation * 5;
    });

    return Math.max(0, Math.min(100, Math.round(score)));
  }

  /**
   * Randomize standard depth chart (non-baseball fielding)
   */
  private static randomizeStandardDepthChart(
    players: Player[],
    positions: Position[],
    currentDepthChart: DepthChartEntry[],
    options: RandomizeOptions
  ): RandomizationResult {
    const { sport, teamId, activeTab, preserveColors = false, absentPlayerIds = [], selectedFormation, standardProtectedPositions = {} } = options;
    const ruleset = SPORT_RULESETS[sport];
    const depthStrings = ruleset.depthStrings.defaultLabels;
    
    // Filter out absent players
    const availablePlayers = players.filter(player => !absentPlayerIds.includes(player.id));
    const newEntries: DepthChartEntry[] = [];
    
    // Calculate total slots needed
    const totalSlots = positions.length * depthStrings.length;
    console.log('[randomizeStandardDepthChart] Total slots to fill:', totalSlots, 'Available players:', availablePlayers.length);
    
    // Create a master assignment list ensuring all players get used first
    const assignments: { player: Player; position: string; depth: number }[] = [];
    
    // Step 0: Handle protected positions first
    const protectedPlayerIds = Object.values(standardProtectedPositions);
    const protectedPositions = Object.keys(standardProtectedPositions);
    
    console.log('[randomizeStandardDepthChart] Protected positions:', protectedPositions);
    console.log('[randomizeStandardDepthChart] Protected players:', protectedPlayerIds);
    
    // Assign protected players to their positions at the first depth level
    protectedPositions.forEach(positionKey => {
      const playerId = standardProtectedPositions[positionKey];
      const player = availablePlayers.find(p => p.id === playerId);
      if (player) {
        assignments.push({
          player,
          position: positionKey,
          depth: depthStrings[0].id // Assign to first depth level
        });
        console.log(`[randomizeStandardDepthChart] Protected: ${player.firstName} ${player.lastName} to ${positionKey} (depth ${depthStrings[0].id})`);
      }
    });
    
    // Remove protected players from available pool for randomization
    const playersForRandomization = availablePlayers.filter(player => !protectedPlayerIds.includes(player.id));
    console.log('[randomizeStandardDepthChart] Players available for randomization:', playersForRandomization.length);
    
    // Step 1: Ensure every remaining player gets assigned at least once
    const shuffledPlayers = this.shuffleArray([...playersForRandomization]);
    const shuffledPositions = this.shuffleArray([...positions.filter(pos => !protectedPositions.includes(pos.key))]);
    const shuffledDepths = this.shuffleArray([...depthStrings]);
    
    console.log('[randomizeStandardDepthChart] Step 1: Assigning all', shuffledPlayers.length, 'players to ensure everyone gets used');
    
    shuffledPlayers.forEach((player, index) => {
      // Assign each player to a position/depth combination
      const positionIndex = index % shuffledPositions.length;
      const depthIndex = Math.floor(index / shuffledPositions.length) % shuffledDepths.length;
      
      const position = shuffledPositions[positionIndex];
      const depth = shuffledDepths[depthIndex];
      
      assignments.push({
        player,
        position: position.key,
        depth: depth.id
      });
      
      console.log(`[randomizeStandardDepthChart] Assigned ${player.firstName} ${player.lastName} to ${position.key} (depth ${depth.id})`);
    });
    
    // Step 2: Fill remaining slots by cycling through players again
    const assignedSlots = assignments.length;
    const remainingSlots = totalSlots - assignedSlots;
    
    console.log('[randomizeStandardDepthChart] Step 2: Filling', remainingSlots, 'remaining slots');
    
    if (remainingSlots > 0) {
      // Get all position/depth combinations that haven't been filled
      const allSlots: { position: string; depth: number }[] = [];
      positions.forEach(position => {
        depthStrings.forEach(depthString => {
          allSlots.push({ position: position.key, depth: depthString.id });
        });
      });
      
      // Remove already assigned slots
      const remainingSlotOptions = allSlots.filter(slot => 
        !assignments.some(assignment => 
          assignment.position === slot.position && assignment.depth === slot.depth
        )
      );
      
      // Shuffle remaining slots and fill them
      const shuffledRemainingSlots = this.shuffleArray(remainingSlotOptions);
      
      for (let i = 0; i < remainingSlots && i < shuffledRemainingSlots.length; i++) {
        const slot = shuffledRemainingSlots[i];
        const playerIndex = i % playersForRandomization.length;
        const player = playersForRandomization[playerIndex];
        
        assignments.push({
          player,
          position: slot.position,
          depth: slot.depth
        });
        
        console.log(`[randomizeStandardDepthChart] Filled remaining slot: ${player.firstName} ${player.lastName} to ${slot.position} (depth ${slot.depth})`);
      }
    }
    
    // Step 3: Convert assignments to depth chart entries
    assignments.forEach(assignment => {
      // Get existing color if preserving colors
      const existingEntry = currentDepthChart.find(
        entry => entry.position === assignment.position && 
                entry.depth === assignment.depth && 
                entry.unit === activeTab
      );
      
      const entry: DepthChartEntry = {
        team_id: teamId,
        position: assignment.position,
        depth: assignment.depth,
        unit: activeTab,
        player_id: assignment.player.id,
        color: preserveColors ? (existingEntry?.color || '') : ''
      };
      
      // Add formation field if sport uses formations
      if (ruleset.formations.enabled) {
        entry.formation = selectedFormation || '';
        console.log('[randomizeStandardDepthChart] Setting formation field:', entry.formation, 'for position:', entry.position, 'depth:', assignment.depth);
      } else {
        console.log('[randomizeStandardDepthChart] Sport does not use formations, omitting formation field');
      }
      
      newEntries.push(entry);
    });

    console.log('[randomizeStandardDepthChart] Final result:', newEntries.length, 'total entries, all', availablePlayers.length, 'players used');
    
    return {
      entries: newEntries,
      statistics: {
        totalPositionsAssigned: newEntries.length,
        playersAssigned: availablePlayers.length, // All available players are guaranteed to be used
        benchedPlayers: 0 // No one is benched since everyone gets assigned
      }
    };
  }

  /**
   * Randomize lineup order (for baseball/softball lineup tab)
   */
  static randomizeLineupOrder(
    players: Player[],
    positions: Position[],
    currentDepthChart: DepthChartEntry[],
    options: RandomizeOptions
  ): RandomizationResult {
    const { teamId, activeTab, preserveColors = false, absentPlayerIds = [], selectedFormation } = options;
    
    console.log('[randomizeLineupOrder] Using activeTab (database unit):', activeTab);
    
    // Filter out absent players; main roster (non-subs) bat first, then subs (each group shuffled)
    const availablePlayers = players.filter(player => !absentPlayerIds.includes(player.id));
    const mainRoster = availablePlayers.filter(p => !(p as any).is_sub);
    const subs = availablePlayers.filter(p => (p as any).is_sub);
    const shuffledPlayers = [...this.shuffleArray(mainRoster), ...this.shuffleArray(subs)];
    const newEntries: DepthChartEntry[] = [];

    console.log('[randomizeLineupOrder] Available players:', availablePlayers.length);
    console.log('[randomizeLineupOrder] Positions array length:', positions.length);

    // For youth baseball/softball, everyone bats - create entries for all available players
    // Don't limit by positions array length
    shuffledPlayers.forEach((player, index) => {
      const battingOrder = index + 1; // 1-based batting order
      
      // Get existing color if preserving colors
      const existingEntry = currentDepthChart.find(
        entry => entry.position === battingOrder.toString() && 
                entry.depth === 1 && 
                entry.unit === activeTab
      );
      
      const entry: DepthChartEntry = {
        team_id: teamId,
        position: battingOrder.toString(), // Use batting order number as position
        depth: 1, // Lineup always uses depth 1
        unit: activeTab, // This should now be 'Offense' for baseball lineup
        player_id: player.id,
        color: preserveColors ? (existingEntry?.color || '') : ''
      };
      
      // Add formation field if sport uses formations
      const ruleset = SPORT_RULESETS[options.sport];
      if (ruleset.formations.enabled) {
        // Use the current selected formation from options
        entry.formation = selectedFormation || '';
        console.log('[randomizeLineupOrder] Setting formation field:', entry.formation, 'for batting order:', entry.position);
      } else {
        console.log('[randomizeLineupOrder] Sport does not use formations, omitting formation field');
      }
      
      newEntries.push(entry);
    });

    console.log('[randomizeLineupOrder] Created entries for', newEntries.length, 'players');
    console.log('[randomizeLineupOrder] First entry unit:', newEntries[0]?.unit);
    
    return {
      entries: newEntries,
      statistics: {
        totalPositionsAssigned: newEntries.length,
        playersAssigned: newEntries.length,
        benchedPlayers: 0 // No one is benched in the lineup - everyone bats
      }
    };
  }

  /**
   * Fisher-Yates shuffle algorithm for randomizing arrays
   */
  private static shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * Validate randomization parameters
   */
  static validateRandomizationParams(
    players: Player[],
    positions: Position[],
    options: RandomizeOptions
  ): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const { absentPlayerIds = [] } = options;

    if (!players || players.length === 0) {
      errors.push('No players available for randomization');
    }

    const availablePlayers = players.filter(player => !absentPlayerIds.includes(player.id));
    if (availablePlayers.length === 0) {
      errors.push('No available players for randomization (all players are absent)');
    }

    if (!positions || positions.length === 0) {
      errors.push('No positions available for randomization');
    }

    if (!options.sport || !SPORT_RULESETS[options.sport]) {
      errors.push('Invalid or unsupported sport');
    }

    if (!options.activeTab) {
      errors.push('Active tab is required');
    }

    if (!options.teamId) {
      errors.push('Team ID is required');
    }

    // Additional validation for baseball fielding with limited players.
    // DH only takes a roster spot when a DH player is explicitly set.
    if ((options.sport === 'Baseball' || options.sport === 'Softball') && options.activeTab === 'Fielding') {
      const defense = SPORT_POSITIONS[options.sport]['Defense'] || [];
      const hasDh = defense.some(p => p.key === 'DH');
      const dhInUse = Boolean(
        (options as any).protectedDhPlayerId &&
          !absentPlayerIds.includes((options as any).protectedDhPlayerId) &&
          hasDh
      );
      const required = dhInUse ? defense.length : defense.filter(p => p.key !== 'DH').length;
      if (availablePlayers.length < required) {
        errors.push(`Need at least ${required} available players for fielding positions (currently have ${availablePlayers.length})`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get randomization preview information
   */
  static getRandomizationPreview(
    players: Player[],
    positions: Position[],
    options: RandomizeOptions & { teamInnings?: number }
  ): {
    willRandomize: number;
    willBench: number;
    strategy: string;
  } {
    const { sport, activeTab, absentPlayerIds = [], teamInnings } = options;
    const ruleset = SPORT_RULESETS[sport];

    // Filter out absent players for calculations
    const availablePlayers = players.filter(player => !absentPlayerIds.includes(player.id));

    if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Fielding') {
      // DH is an optional off-diamond slot; when no DH is set it doesn't take a field spot.
      const defense = SPORT_POSITIONS[sport]['Defense'] || [];
      const hasDh = defense.some(p => p.key === 'DH');
      const dhInUse = Boolean(
        (options as any).protectedDhPlayerId &&
          !absentPlayerIds.includes((options as any).protectedDhPlayerId) &&
          hasDh
      );
      const fieldingSlots = dhInUse ? defense.length : defense.filter(p => p.key !== 'DH').length;
      const defaultInnings = teamInnings || ruleset.defaultInnings || 7;

      return {
        willRandomize: availablePlayers.length,
        willBench: Math.max(0, availablePlayers.length - fieldingSlots),
        strategy: `Rotating ${Math.min(availablePlayers.length, fieldingSlots)} players across ${defaultInnings} innings${absentPlayerIds.length > 0 ? ` (${absentPlayerIds.length} absent)` : ''}`
      };
    }

    if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Lineup') {
      return {
        willRandomize: availablePlayers.length,
        willBench: 0, // No one is benched in lineup - everyone bats
        strategy: `Randomizing batting order for all ${availablePlayers.length} players${absentPlayerIds.length > 0 ? ` (${absentPlayerIds.length} absent)` : ''}`
      };
    }

    // Standard depth chart
    const depthStrings = ruleset.depthStrings.defaultLabels;
    const totalSlots = positions.length * depthStrings.length;
    
    return {
      willRandomize: totalSlots, // All slots will be filled
      willBench: 0, // No one is benched since players can appear multiple times
      strategy: `Filling all ${totalSlots} positions (${positions.length} positions × ${depthStrings.length} depth levels) using ${availablePlayers.length} players${availablePlayers.length < totalSlots ? ' (players will appear multiple times)' : ''}${absentPlayerIds.length > 0 ? ` (${absentPlayerIds.length} absent)` : ''}`
    };
  }
} 